import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import wifiCellularMismatchClient from "@/apis/wifiCellularMismatchClient";
import IcomoonIcon from "@icons/IcomoonIcon";
import SntDeleteIcon from "@icons/SntDeleteIcon";
import ActionToolEnum from "@/containers/AdminLayout/Toolbar/ActionToolEnum";
import ActionToolType from "@/containers/AdminLayout/Toolbar/ActionToolType";
import SntDateView from "@/components/ReactBootstrap/SntDateView";
import SntLink from "@/components/ReactBootstrap/SntLink";
import SntText from "@/components/ReactBootstrap/SntText";
import { DefaultTag } from "@cleanup/components/TagMore";
import convert from "convert-units";
import SntPencilIcon from "@icons/SntPencilIcon";
import SntActionButton from "@/cleanup/components/Buttons/SntActionButton";
import SntAdvancedSearchTableViewCard from "@/components/SntTableViewCard/SntAdvancedSearchTableViewCard";
import AdminLayout from "@/containers/AdminLayout/AdminLayout";
import useDeleteAllDialog from "@pages/WifiCellularMismatch/useDeleteAllDialog";
import searchUIClient from "@/apis/searchUIClient";
import { DeviceHomeRoute } from "@/cleanup/utils/routesUtil/routesUtil";
import SntFilterIcon from "@icons/SntFilterIcon";
import SntBulkIcon from "@icons/SntBulkIcon";
import GlobalUtils from "@/utils/GlobalUtils";
import SntButton from "@/cleanup/widgets/forms/SntButton";
import SntDialog from "@/components/SntDialog/SntDialog";
import moment from "moment";
import UpdateWifiCellularMismatchMap from "./UpdateWifiCellularMismatchMap";
import Loading from "@/components/ReactBootstrap/LoaderSpinner/Loading";
import SntRadio from "@cleanup/components/SntRadio/SntRadio.tsx";
import SntInfoPanel from "@cleanup/components/SntInfo/SntInfoPanel";
import SntOnOffSwitch from "@/cleanup/wrappers/Switch/SntOnOffSwitch";
import TowerHarvestedIcon from "@images/map/Cell_tower_harvested.svg";
import TowerLocationiqIcon from "@images/map/Cell_tower_locationiq.svg";
import TowerOpencellIcon from "@images/map/Cell_tower_opencellid.svg";
import TowerHereIcon from "@images/map/Cell_tower_here.svg";
import TowerLiveLocationiqIcon from "@images/map/Cell_tower_live_locationiq.svg";
import TowerLiveOpencellIcon from "@images/map/Cell_tower_live_opencellid.svg";
import TowerLiveHereIcon from "@images/map/Cell_tower_live_here.svg";
import WifiIcon from "@images/map/Wi-Fi.svg";
import StopIcon from "@images/map/stop.svg";
import { VerticalLayout } from "@/cleanup/components/FormValidation";
import { RootState } from "@/types/RootState";
import { hasPermission } from "@/hooks/usePermissions";
import { UserPermission } from "@/types/generated/UserPermission";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";
import CellTowerAnalysisDialog from "./CellTowerAnalysisDialog";
import WifiCellularMismatchCleanupDialog from "./WifiCellularMismatchCleanupDialog";
import styled from "styled-components";

const LegendStyle = styled.div`
  @keyframes pulse-glow {
    0%, 100% { filter: drop-shadow(0 0 4px #ff5722) drop-shadow(0 0 8px #ff5722); }
    50% { filter: drop-shadow(0 0 8px #ff5722) drop-shadow(0 0 16px #ff5722); }
  }
`;

const PAGE_NAME = "WIFI_CELLULAR_MISMATCH";
const DEFAULT_OPTION = {
  pageName: PAGE_NAME,
  queryObjectType: "WIFI_CELLULAR_MISMATCH",
  filtersVisibleAtInit: ["deviceSerial", "cellId", "createdAt"],
  optionsSettings: {},
};

interface TableRef {
  setLoading: (loading: boolean) => void;
  updateDataTable: (data: Record<string, unknown>) => void;
  getDataCallBack: (
    params: Record<string, unknown>,
    dataOptions: Record<string, unknown>,
    data: Record<string, unknown>
  ) => [unknown, unknown];
  resetDefault: () => void;
  refresh: () => void;
}

interface MismatchItem {
  id: number;
  cellId?: string;
  deviceSerial?: string;
  wifiProvider?: string;
  macs?: string[];
  wifiLat?: number;
  wifiLng?: number;
  cellLat?: number;
  cellLng?: number;
  network?: string;
  distance?: number;
  createdAt?: string;
  name?: string;
  mcc?: string;
  net?: string;
  area?: string;
  cell?: string;
  country?: string;
  wifiHull?: { x: number; y: number }[];
  trustedLocations?: {
    lat: number;
    lng: number;
    source: string;
    timestamp: string;
  }[];
  device?: { serial?: string };
  harvestedLat?: number;
  harvestedLng?: number;
  harvestedObservationCount?: number;
  harvestedConfidence?: string;
  lacTowers?: {
    cellId?: number;
    lat?: number;
    lng?: number;
    observationCount?: number;
    confidence?: string;
  }[];
}

interface CellProps {
  cell: {
    row: {
      original: MismatchItem;
    };
  };
  value: unknown;
}

interface Column {
  key: string;
  title: string;
  disableSortBy?: boolean;
  Cell?: (props: CellProps) => React.ReactNode;
  cellCopyData?: (data: MismatchItem) => string[];
  cellFilterValue?: (data: MismatchItem) => string[];
  immovable?: boolean;
  canResize?: boolean;
}

interface FilterQuery {
  categoryId: string;
  filterKey: string;
  filterType: string;
  searchType: string;
  notFilter: boolean;
  filterValue: {
    searchText?: string;
  };
}

interface OptionsSettings {
  query: FilterQuery[];
  queryObjectType?: string;
  startIndex?: number | null;
  maxResults?: number | null;
  requestedTableColumns?: { columnKey: string; order: null }[];
}

interface NewCellTowerLocation {
  latitude: number;
  longitude: number;
}

interface ForceFilterAction {
  action: string;
  key: string;
  filterKeys: string[];
  query: FilterQuery[];
}

interface LocationMismatchProps {
  reload?: unknown;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
}

const LocationMismatch = ({ reload }: LocationMismatchProps) => {
  //redux state
  const table = useRef<TableRef | null>(null);
  const [showUpdatePopup, setShowUpdatePopup] = useState(false);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);

  const [reloadSessionState, setReloadSessionState] = useState(false);
  const loginInfo = useSelector((state: RootState) => state.user);
  const { unitDistance } = loginInfo;

  const [forceFilterAction, setForceFilterAction] =
    useState<ForceFilterAction | null>(null);
  const [filterMap, setFilterMap] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [showResolveAll, setShowResolveAll] = useState(false);

  const [items, setItems] = useState<MismatchItem[]>([]);
  const [isShowResponse, setShowResponse] = useState(false);
  const [responseText, setResponseText] = useState("");
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [decisionOption, setDecisionOption] = useState(1); // Default: delay decision
  const [newCellTowerLocation, setNewCellTowerLocation] =
    useState<NewCellTowerLocation | null>(null);

  // Analysis dialog
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);

  // Cleanup dialog
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);

  // Page header toolbar actions
  const toolList = useMemo(() => {
    if (!hasPermission(UserPermission.CELL_TOWER_TROUBLESHOOTING_VIEW)) {
      return [];
    }
    const tools: {
      id: string;
      title: string;
      type: string;
      icon: React.ReactElement;
      isSelectable: boolean;
    }[] = [
      {
        id: ActionToolEnum.cell_tower_analysis,
        title: translate(LanguageKey.cell_tower_analysis_key),
        type: ActionToolType.data,
        icon: <IcomoonIcon icon="stats-bars" size="16" />,
        isSelectable: false,
      },
    ];

    if (hasPermission(UserPermission.CELL_TOWER_TROUBLESHOOTING_MANAGE)) {
      tools.push({
        id: ActionToolEnum.cell_tower_cleanup,
        title: translate(LanguageKey.cell_tower_cleanup_key),
        type: ActionToolType.data,
        icon: <IcomoonIcon icon="bin" size="16" />,
        isSelectable: false,
      });
    }

    return tools;
  }, []);

  const handleActionData = (actionId: string) => {
    if (actionId === ActionToolEnum.cell_tower_analysis) {
      setShowAnalysisDialog(true);
    } else if (actionId === ActionToolEnum.cell_tower_cleanup) {
      setShowCleanupDialog(true);
    }
  };

  // Map display options
  const [showWifi, setShowWifi] = useState(true);
  const [showLocationiq, setShowLocationiq] = useState(true);
  const [showOpencell, setShowOpencell] = useState(true);
  const [showHere, setShowHere] = useState(true);
  const [showAdjacentTrustedPoints, setShowAdjacentTrustedPoints] =
    useState(false);
  const [showHarvestedCellTower, setShowHarvestedCellTower] = useState(true);
  const [showHarvestedLac, setShowHarvestedLac] = useState(true);

  // Compute which layers have data available
  const hasWifiData = useMemo(() => items.some((item) => item.wifiLat && item.wifiLng), [items]);
  const hasTrustedPointsData = useMemo(
    () => items.some((item) => item.trustedLocations && item.trustedLocations.length > 0),
    [items]
  );
  const hasLocationiqData = useMemo(
    () => items.some((item) => item.wifiProvider === "locationIQ" && item.cellLat && item.cellLng),
    [items]
  );
  const hasOpencellData = useMemo(
    () => items.some((item) => item.wifiProvider === "openCellId" && item.cellLat && item.cellLng),
    [items]
  );
  const hasHereData = useMemo(
    () => items.some((item) => item.wifiProvider === "here" && item.cellLat && item.cellLng),
    [items]
  );
  const hasHarvestedCellTowerData = useMemo(
    () => !!(items[0]?.harvestedLat && items[0]?.harvestedLng),
    [items]
  );
  const hasHarvestedLacData = useMemo(
    () => !!(items[0]?.lacTowers && items[0]?.lacTowers.length > 0),
    [items]
  );

  const allColumns = useMemo<Column[]>(
    () => [
      {
        key: "cellId",
        title: translate(LanguageKey.cell_id),
        Cell: ({ value }: CellProps) => {
          return <SntText value={value as string}></SntText>;
        },
      },
      {
        key: "deviceSerial",
        title: translate(LanguageKey.serial_key),
        Cell: ({ value }: CellProps) => {
          return value ? (
            <SntLink
              to={DeviceHomeRoute.getUpdatePage({
                serial: value as string,
              })}
            >
              {value as string}
            </SntLink>
          ) : (
            ""
          );
        },
      },
      {
        key: "wifiProvider",
        title: translate(LanguageKey.wifi_provider_key),
        Cell: ({ value }: CellProps) => {
          return (value as string) || "";
        },
      },
      {
        key: "macs",
        title: translate(LanguageKey.wifi_mac_addresses_key),
        Cell: ({ value }: CellProps) => {
          return (
            (value as string[])?.map((m) => (
              <DefaultTag key={m} tagName={m} className="me-1 mb-1" />
            )) || ""
          );
        },
      },
      {
        key: "wifiLocation",
        title: translate(LanguageKey.journey_wifi_location_key),
        disableSortBy: true,
        Cell: ({ cell }: CellProps) => {
          const full = cell.row.original;
          return full.wifiLat && full.wifiLng ? (
            <>
              <div className="d-flex">
                latitude: {full.wifiLat} &nbsp;
                <a
                  target=" "
                  href={
                    "http://maps.google.com/maps?q=loc:" +
                    full.wifiLat +
                    "," +
                    full.wifiLng
                  }
                >
                  <IcomoonIcon
                    icon="map2"
                    size="12"
                    color="#6EDC85"
                  ></IcomoonIcon>
                </a>
              </div>
              <div>longitude: {full.wifiLng}</div>
            </>
          ) : (
            ""
          );
        },
        cellCopyData: (data: MismatchItem) => {
          let text = "";
          if (data.wifiLat != undefined && data.wifiLat != null) {
            text += data.wifiLat + " - ";
          }
          if (data.wifiLng != undefined && data.wifiLng != null) {
            text += data.wifiLng + " - ";
          }
          return [text];
        },
        cellFilterValue: (data: MismatchItem) => {
          let text = "";
          if (data.wifiLat != undefined && data.wifiLat != null) {
            text += data.wifiLat + " - ";
          }
          if (data.wifiLng != undefined && data.wifiLng != null) {
            text += data.wifiLng + " - ";
          }
          return [text];
        },
      },
      {
        key: "cellularInfo",
        title: translate(LanguageKey.cellular_tower_info),
        Cell: ({ cell }: CellProps) => {
          const full = cell.row.original;
          const info = GlobalUtils.formatCellularTowerInfo(full, "");
          return <div>{info}</div>;
        },
        cellCopyData: (data: MismatchItem) => {
          let text = "";
          if (data.cellLat != undefined && data.cellLat != null) {
            text += data.cellLat + " - ";
          }
          if (data.cellLng != undefined && data.cellLng != null) {
            text += data.cellLng + " - ";
          }
          return [text];
        },
        cellFilterValue: (data: MismatchItem) => {
          let text = "";
          if (data.cellLat != undefined && data.cellLat != null) {
            text += data.cellLat + " - ";
          }
          if (data.cellLng != undefined && data.cellLng != null) {
            text += data.cellLng + " - ";
          }
          return [text];
        },
      },
      {
        key: "cellularProvider",
        title: translate(LanguageKey.cellular_provider_key),
        Cell: ({ cell }: CellProps) => {
          const full = cell.row.original;
          return full.network || "";
        },
      },
      {
        key: "cellularLocation",
        title: translate(LanguageKey.cellular_tower_location),
        disableSortBy: true,
        Cell: ({ cell }: CellProps) => {
          const full = cell.row.original;
          return full.cellLat && full.cellLng ? (
            <>
              <div className="d-flex">
                latitude: {full.cellLat} &nbsp;
                <a
                  target=" "
                  href={
                    "http://maps.google.com/maps?q=loc:" +
                    full.cellLat +
                    "," +
                    full.cellLng
                  }
                >
                  <IcomoonIcon
                    icon="map2"
                    size="12"
                    color="#6EDC85"
                  ></IcomoonIcon>
                </a>
              </div>
              <div>longitude: {full.cellLng}</div>
            </>
          ) : (
            ""
          );
        },
        cellCopyData: (data: MismatchItem) => {
          let text = "";
          if (data.cellLat != undefined && data.cellLat != null) {
            text += data.cellLat + " - ";
          }
          if (data.cellLng != undefined && data.cellLng != null) {
            text += data.cellLng + " - ";
          }
          return [text];
        },
        cellFilterValue: (data: MismatchItem) => {
          let text = "";
          if (data.cellLat != undefined && data.cellLat != null) {
            text += data.cellLat + " - ";
          }
          if (data.cellLng != undefined && data.cellLng != null) {
            text += data.cellLng + " - ";
          }
          return [text];
        },
      },
      {
        key: "distance",
        title: translate(LanguageKey.distance_key),
        Cell: ({ value }: CellProps) => {
          if (value) {
            if (unitDistance === "KILOMETER") {
              return (
                Math.round(convert(value as number).from("m").to("km")) +
                " " +
                translate(LanguageKey.maintenance_unit_KM_key)
              );
            } else {
              return (
                Math.round(convert(value as number).from("m").to("mi")) +
                " " +
                translate(LanguageKey.maintenance_unit_MI_key)
              );
            }
          } else {
            return "";
          }
        },
      },
      {
        key: "createdAt",
        title: translate(LanguageKey.created_date_key),
        Cell: ({ value }: CellProps) => {
          return (
            <SntDateView
              value={value as string}
              other={translate(LanguageKey.na_key)}
              format="lll"
            ></SntDateView>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const defaultColumnInit = useMemo(
    () => [
      "deviceSerial",
      "wifiProvider",
      "macs",
      "wifiLocation",
      "cellularInfo",
      "cellularProvider",
      "cellularLocation",
      "distance",
      "createdAt",
    ],
    []
  );

  const onDeleteClicked = (
    el: React.MouseEvent<HTMLButtonElement>,
    item: MismatchItem
  ) => {
    el.preventDefault();
    handleOpen(
      item,
      item.name &&
        translate(LanguageKey.delete_message_with_name_key).replace("{0}", item.name)
    );
  };

  const onFilterClicked = (item: MismatchItem) => {
    let oldOptionsSettings: OptionsSettings | null = null;
    const storedSettings = sessionStorage.getItem(PAGE_NAME);
    if (storedSettings) {
      oldOptionsSettings = JSON.parse(storedSettings) as OptionsSettings;
    }
    if (oldOptionsSettings) {
      oldOptionsSettings.query = [
        {
          categoryId: "info",
          filterKey: "cellId",
          filterType: "MULTI_STRING",
          searchType: "STATIC",
          notFilter: false,
          filterValue: {
            searchText: item.cellId,
          },
        },
        {
          categoryId: "info",
          filterKey: "mcc",
          filterType: "MULTI_STRING",
          searchType: "STATIC",
          notFilter: false,
          filterValue: {
            searchText: item.mcc,
          },
        },
        {
          categoryId: "info",
          filterKey: "net",
          filterType: "MULTI_STRING",
          searchType: "STATIC",
          notFilter: false,
          filterValue: {
            searchText: item.net,
          },
        },
        {
          categoryId: "info",
          filterKey: "area",
          filterType: "MULTI_STRING",
          searchType: "STATIC",
          notFilter: false,
          filterValue: {
            searchText: item.area,
          },
        },
      ];
      sessionStorage.setItem(PAGE_NAME, JSON.stringify(oldOptionsSettings));
    }
    setReloadSessionState((pre) => !pre);
  };

  const extraCols = useMemo<Column[]>(
    () => [
      {
        key: "actions",
        title: translate(LanguageKey.actions_key),
        immovable: true,
        disableSortBy: true,
        canResize: false,
        Cell: ({ cell }: CellProps) => {
          const full = cell.row.original;
          return (
            <div>
              {hasPermission(UserPermission.CELL_TOWER_TROUBLESHOOTING_MANAGE) && (
                <>
                  <SntActionButton
                    title={translate(LanguageKey.add_as_filterkey)}
                    onClick={() => {
                      onFilterClicked(full);
                    }}
                  >
                    <SntFilterIcon />
                  </SntActionButton>

                  <SntActionButton
                    title={translate(LanguageKey.edit_key)}
                    onClick={() => {
                      setShowUpdatePopup(true);
                      setSelectedItems([full.id]);
                    }}
                  >
                    <SntPencilIcon />
                  </SntActionButton>

                  <SntActionButton
                    title={translate(LanguageKey.delete_key)}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                      onDeleteClicked(e, full)
                    }
                  >
                    <SntDeleteIcon />
                  </SntActionButton>
                </>
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onDataRequestChange = (
    data: { params: Record<string, unknown>; dataOptions: Record<string, unknown> },
    callback: (a: unknown, b: unknown) => void
  ) => {
    const { params, dataOptions } = data;
    if (table.current) {
      table.current.setLoading(true);
      searchUIClient.search(params).then(
        ({ data }: { data: Record<string, unknown> }) => {
          if (table.current) {
            table.current.updateDataTable({
              ...data,
              pageCount: data.countByFilter,
              totalItem: {
                countByFilter: data.countByFilter,
              },
            });
            table.current.setLoading(false);

            const dataCallback = table.current.getDataCallBack(
              params,
              dataOptions,
              data
            );
            callback(dataCallback[0], dataCallback[1]);
          }
          checkToShowResolveAllButton();
        },
        () => {
          if (table.current) {
            table.current.setLoading(false);
          }
        }
      );
    }
  };

  const { handleOpen, handleClose, handleOpenError, DeleteDialog } =
    useDeleteAllDialog({
      onDelete: (itemSelected: unknown, deleteAll: boolean) => {
        wifiCellularMismatchClient
          .delete((itemSelected as MismatchItem).id, deleteAll)
          .then(() => {
            handleClose();
            if (table.current) {
              table.current.resetDefault();
            }
          })
          .catch((error: ApiError) => {
            handleClose();
            let message = "";
            if (
              error &&
              error.response &&
              error.response.data &&
              error.response.data.message
            ) {
              message = error.response.data.message;
            }
            handleOpenError(message);
          });
      },
      allTitle: translate(LanguageKey.delete_all_mismatches_key),
    });

  const onUpdateDone = () => {
    if (table.current) {
      table.current.refresh();
    }
  };

  const checkToShowResolveAllButton = () => {
    //Check if 4 filter cellId,mcc,net existed and have filterValue -> show resolve all button
    let oldOptionsSettings: OptionsSettings | null = null;
    const storedSettings = sessionStorage.getItem(PAGE_NAME);
    if (storedSettings) {
      oldOptionsSettings = JSON.parse(storedSettings) as OptionsSettings;
    }
    if (!oldOptionsSettings) {
      setShowResolveAll(false);
      return;
    }
    const listFilters: FilterQuery[] = [];
    for (let i = 0; i <= oldOptionsSettings.query.length; i++) {
      if (
        oldOptionsSettings.query[i]?.filterKey == "cellId" ||
        oldOptionsSettings.query[i]?.filterKey == "mcc" ||
        oldOptionsSettings.query[i]?.filterKey == "net" ||
        oldOptionsSettings.query[i]?.filterKey == "area"
      ) {
        listFilters.push(oldOptionsSettings.query[i]);
      }
    }
    let countFilterValue = 0;
    if (listFilters.length === 4) {
      for (let i = 0; i <= listFilters.length; i++) {
        if (listFilters[i]?.filterValue) {
          countFilterValue++;
        }
      }
      if (countFilterValue == 4) {
        setShowResolveAll(true);
        return;
      }
    }
    setShowResolveAll(false);
  };

  const onResolveAllClick = () => {
    let optionsSettings: OptionsSettings | null = null;
    const storedSettings = sessionStorage.getItem(PAGE_NAME);
    if (storedSettings) {
      optionsSettings = JSON.parse(storedSettings) as OptionsSettings;
    }

    if (!optionsSettings) return;

    const fullFilter: OptionsSettings = {
      query: optionsSettings.query,
      queryObjectType: "WIFI_CELLULAR_MISMATCH",
      startIndex: null,
      maxResults: null,
      requestedTableColumns: [
        {
          columnKey: "id",
          order: null,
        },
      ],
    };

    searchUIClient
      .search(fullFilter)
      .then(({ data }: { data: { data: MismatchItem[] } }) => {
        const listMismatch = data.data;
        const results: number[] = [];
        for (let i = 0; i < listMismatch.length; i++) {
          results.push(listMismatch[i].id);
        }
        setSelectedItems(results);
        setShowUpdatePopup(true);
      })
      .catch((error: unknown) => {
        console.log(error);
      });
  };

  useEffect(() => {
    if (selectedItems?.length) {
      setIsLoadingItems(true);
      // Reset decision state when loading new items
      setDecisionOption(1);
      setNewCellTowerLocation(null);

      wifiCellularMismatchClient
        .getByIds(selectedItems.slice(0, 100))
        .then((response: { data: MismatchItem[] }) => {
          setItems(response.data);
          setIsLoadingItems(false);
        })
        .catch(() => {
          setIsLoadingItems(false);
        });
    }
  }, [selectedItems]);

  const handleDecision = () => {
    if (!items[0]) return;

    setIsUpdating(true);

    switch (decisionOption) {
      case 1: // Delay decision (close dialog without action)
        setIsUpdating(false);
        break;

      case 2: // Keep current tower location (add to private, clear mismatches)
        wifiCellularMismatchClient
          .delete(items[0].id, true, true)
          .then(() => {
            setResponseText(translate(LanguageKey.update_success_key));
            onUpdateDone();
          })
          .catch((e: ApiError) => {
            setResponseText(
              translate(LanguageKey.save_fail) + ". " + (e.response?.data?.message || "")
            );
          })
          .finally(() => {
            setIsUpdating(false);
            setShowResponse(true);
          });
        break;

      case 3: // Use new cell tower location (save to private, clear mismatches)
        if (newCellTowerLocation) {
          wifiCellularMismatchClient
            .createPrivateCellularTower(items[0].id, {
              lat: newCellTowerLocation.latitude,
              lng: newCellTowerLocation.longitude,
            })
            .then(() => {
              setResponseText(translate(LanguageKey.update_success_key));
              onUpdateDone();
            })
            .catch((e: ApiError) => {
              setResponseText(
                translate(LanguageKey.save_fail) + ". " + (e.response?.data?.message || "")
              );
            })
            .finally(() => {
              setIsUpdating(false);
              setShowResponse(true);
            });
        } else {
          setIsUpdating(false);
          setResponseText(translate(LanguageKey.please_select_location_on_map_key));
          setShowResponse(true);
        }
        break;

      case 4: // Clear mismatches without adding to private
        wifiCellularMismatchClient
          .delete(items[0].id, true, false)
          .then(() => {
            setResponseText(translate(LanguageKey.update_success_key));
            onUpdateDone();
          })
          .catch((e: ApiError) => {
            setResponseText(
              translate(LanguageKey.save_fail) + ". " + (e.response?.data?.message || "")
            );
          })
          .finally(() => {
            setIsUpdating(false);
            setShowResponse(true);
          });
        break;

      default:
        setIsUpdating(false);
        break;
    }

    setShowUpdatePopup(false);
  };

  return (
    <div>
      <DeleteDialog />

      <CellTowerAnalysisDialog
        isShow={showAnalysisDialog}
        onClose={() => setShowAnalysisDialog(false)}
      />

      <WifiCellularMismatchCleanupDialog
        isShow={showCleanupDialog}
        onClose={() => setShowCleanupDialog(false)}
        onCleanupComplete={() => {
          if (table.current) {
            table.current.refresh();
          }
        }}
      />

      <SntDialog
        isShow={showUpdatePopup}
        onSave={handleDecision}
        onClose={() => setShowUpdatePopup(false)}
        title={translate(LanguageKey.resolve_wifi_cellular_mismatch_key)}
        saveTxt={translate(LanguageKey.save_key)}
        closeTxt={translate(LanguageKey.cancel_key)}
        customModalClassname={"snt-config-column-modal"}
        disabled={isUpdating}
      >
        {() => (
          <>
            {isLoadingItems ? (
              <Loading />
            ) : (
              <div className="row">
                {/* Left Column - Info, Checkboxes, and Decisions */}
                <div className="col-md-4">
                  {/* Info Section */}
                  <SntInfoPanel className="mb-0">
                    {(() => {
                      if (!items || items.length === 0) return null;

                      const locationPoints = items.length;
                      const uniqueMacs = new Set(
                        items.flatMap((item) => item.macs || [])
                      ).size;

                      // Calculate cellular info
                      let cellularCountryInfo: string | null = null;
                      let cellularInfo: string | null = null;

                      if (items.length > 0) {
                        const firstItem = items[0];
                        if (
                          firstItem.mcc ||
                          firstItem.net ||
                          firstItem.area ||
                          firstItem.cell
                        ) {
                          cellularInfo = GlobalUtils.formatCellularTowerInfo(
                            {
                              mcc: firstItem.mcc,
                              net: firstItem.net,
                              area: firstItem.area,
                              cellId: firstItem.cell,
                            },
                            ""
                          );
                        }
                        if (firstItem.network || firstItem.country) {
                          cellularCountryInfo = `network: ${
                            firstItem.network || translate(LanguageKey.unknown_key)
                          }, country: ${
                            firstItem.country || translate(LanguageKey.unknown_key)
                          }`;
                        }
                      }

                      // Calculate time range
                      let timeRange: string | null = null;
                      if (items.length > 0) {
                        const dates = items
                          .map((item) => moment(item.createdAt))
                          .filter((date) => date.isValid());
                        if (dates.length > 0) {
                          const startTime = moment.min(dates);
                          const endTime = moment.max(dates);

                          if (startTime.isSame(endTime)) {
                            // If only one date or all dates are the same, show just one
                            timeRange =
                              translate(LanguageKey.time_key) +
                              `: ${startTime.format("lll")}`;
                          } else {
                            // Show range when there are different dates
                            timeRange =
                              translate(LanguageKey.time_range_key) +
                              `: ${startTime.format("lll")} - ${endTime.format(
                                "lll"
                              )}`;
                          }
                        }
                      }

                      return (
                        <div>
                          {cellularInfo && (
                            <div>
                              <h6>{translate(LanguageKey.cellular_tower_info)}:</h6>
                              <ul>
                                {cellularCountryInfo ? (
                                  <li>{cellularCountryInfo}</li>
                                ) : (
                                  ""
                                )}
                                <li>{cellularInfo}</li>
                              </ul>
                            </div>
                          )}
                          <div>
                            <h6>{translate(LanguageKey.number_of_cellular_mismatch)}:</h6>
                            <ul>
                              <li>
                                {locationPoints} {translate(LanguageKey.location_points_key)},{" "}
                                {uniqueMacs} {translate(LanguageKey.mac_combinations_key)}
                              </li>
                              {timeRange && <li>{timeRange}</li>}
                            </ul>
                          </div>
                        </div>
                      );
                    })()}
                  </SntInfoPanel>

                  {/* Layer Toggle Section */}
                  <LegendStyle className="mb-3">
                    <div className="d-flex align-items-center" style={{ opacity: hasWifiData ? 1 : 0.4 }}>
                      <div className="d-flex justify-content-center align-items-center" style={{ width: 36, minHeight: 28 }}>
                        <img src={WifiIcon} alt="wifi" width={36} height={36} />
                      </div>
                      <div className="flex-grow-1 ms-2">
                        <SntOnOffSwitch
                          checked={showWifi}
                          onChange={(value: boolean) => setShowWifi(value)}
                          disabled={!hasWifiData}
                          title={translate(LanguageKey.show_wifi_marker_key)}
                        />
                      </div>
                    </div>
                    <div className="d-flex align-items-center" style={{ opacity: hasTrustedPointsData ? 1 : 0.4 }}>
                      <div className="d-flex justify-content-center align-items-center" style={{ width: 36, minHeight: 28 }}>
                        <img src={StopIcon} alt="trusted" width={24} height={24} />
                      </div>
                      <div className="flex-grow-1 ms-2">
                        <SntOnOffSwitch
                          checked={showAdjacentTrustedPoints}
                          onChange={(value: boolean) =>
                            setShowAdjacentTrustedPoints(value)
                          }
                          disabled={!hasTrustedPointsData}
                          title={translate(LanguageKey.show_adjacent_trusted_points_key)}
                        />
                      </div>
                    </div>
                    <div className="d-flex align-items-center" style={{ opacity: hasLocationiqData ? 1 : 0.4 }}>
                      <div className="d-flex justify-content-center align-items-center" style={{ width: 36, minHeight: 28 }}>
                        <img src={TowerLocationiqIcon} alt="locationiq" width={36} height={36} />
                      </div>
                      <div className="flex-grow-1 ms-2">
                        <SntOnOffSwitch
                          checked={showLocationiq}
                          onChange={(value: boolean) => setShowLocationiq(value)}
                          disabled={!hasLocationiqData}
                          title={translate(LanguageKey.show_network_location_locationiq_key)}
                        />
                      </div>
                    </div>
                    <div className="d-flex align-items-center" style={{ marginTop: -4, opacity: hasLocationiqData ? 1 : 0.4 }}>
                      <div className="d-flex justify-content-center align-items-center" style={{ width: 36, minHeight: 22 }}>
                        <img src={TowerLiveLocationiqIcon} alt="locationiq-live" width={28} height={28} />
                      </div>
                      <div className="flex-grow-1 ms-2">
                        <small className="text-muted">= LocationIQ (live)</small>
                      </div>
                    </div>
                    <div className="d-flex align-items-center" style={{ opacity: hasOpencellData ? 1 : 0.4 }}>
                      <div className="d-flex justify-content-center align-items-center" style={{ width: 36, minHeight: 28 }}>
                        <img src={TowerOpencellIcon} alt="opencellid" width={36} height={36} />
                      </div>
                      <div className="flex-grow-1 ms-2">
                        <SntOnOffSwitch
                          checked={showOpencell}
                          onChange={(value: boolean) => setShowOpencell(value)}
                          disabled={!hasOpencellData}
                          title={translate(LanguageKey.show_network_location_opencellid_key)}
                        />
                      </div>
                    </div>
                    <div className="d-flex align-items-center" style={{ marginTop: -4, opacity: hasOpencellData ? 1 : 0.4 }}>
                      <div className="d-flex justify-content-center align-items-center" style={{ width: 36, minHeight: 22 }}>
                        <img src={TowerLiveOpencellIcon} alt="opencellid-live" width={28} height={28} />
                      </div>
                      <div className="flex-grow-1 ms-2">
                        <small className="text-muted">= OpenCellID (live)</small>
                      </div>
                    </div>
                    <div className="d-flex align-items-center" style={{ opacity: hasHereData ? 1 : 0.4 }}>
                      <div className="d-flex justify-content-center align-items-center" style={{ width: 36, minHeight: 28 }}>
                        <img src={TowerHereIcon} alt="here" width={36} height={36} />
                      </div>
                      <div className="flex-grow-1 ms-2">
                        <SntOnOffSwitch
                          checked={showHere}
                          onChange={(value: boolean) => setShowHere(value)}
                          disabled={!hasHereData}
                          title={translate(LanguageKey.show_network_location_here_key)}
                        />
                      </div>
                    </div>
                    <div className="d-flex align-items-center" style={{ marginTop: -4, opacity: hasHereData ? 1 : 0.4 }}>
                      <div className="d-flex justify-content-center align-items-center" style={{ width: 36, minHeight: 22 }}>
                        <img src={TowerLiveHereIcon} alt="here-live" width={28} height={28} />
                      </div>
                      <div className="flex-grow-1 ms-2">
                        <small className="text-muted">= HERE (live)</small>
                      </div>
                    </div>
                    <div className="d-flex align-items-center" style={{ opacity: hasHarvestedCellTowerData ? 1 : 0.4 }}>
                      <div className="d-flex justify-content-center align-items-center" style={{ width: 36, minHeight: 28 }}>
                        <img src={TowerHarvestedIcon} alt="harvested" width={36} height={36} />
                      </div>
                      <div className="flex-grow-1 ms-2">
                        <SntOnOffSwitch
                          checked={showHarvestedCellTower}
                          onChange={(value: boolean) =>
                            setShowHarvestedCellTower(value)
                          }
                          disabled={!hasHarvestedCellTowerData}
                          title={translate(LanguageKey.show_harvested_cell_tower_location_key)}
                        />
                      </div>
                    </div>
                    <div className="d-flex align-items-center" style={{ opacity: hasHarvestedLacData ? 1 : 0.4 }}>
                      <div className="d-flex justify-content-center align-items-center" style={{ width: 36, minHeight: 28 }}>
                        <span style={{ display: "inline-block", width: 18, height: 18, borderRadius: "50%", backgroundColor: "#7b1fa2", border: "2px solid #7b1fa2", opacity: 0.8 }} />
                      </div>
                      <div className="flex-grow-1 ms-2">
                        <SntOnOffSwitch
                          checked={showHarvestedLac}
                          onChange={(value: boolean) =>
                            setShowHarvestedLac(value)
                          }
                          disabled={!hasHarvestedLacData}
                          title={translate(LanguageKey.show_harvested_lac_key)}
                        />
                      </div>
                    </div>
                  </LegendStyle>

                  {/* Decision Panel */}
                  <div className="mt-3">
                    <h6>{translate(LanguageKey.wifi_cellular_mismatch_decision_key)}:</h6>
                    <VerticalLayout>
                      <SntRadio
                        value={1}
                        selectedValue={decisionOption}
                        onChange={(value) => setDecisionOption(Number(value))}
                        label={translate(LanguageKey.delay_decision_dont_know_yet_key)}
                      />

                      <SntRadio
                        value={2}
                        selectedValue={decisionOption}
                        onChange={(value) => setDecisionOption(Number(value))}
                        label={translate(LanguageKey.keep_current_tower_location_key)}
                      />

                      <SntRadio
                        value={3}
                        selectedValue={decisionOption}
                        onChange={(value) => setDecisionOption(Number(value))}
                        label={
                          <span>
                            {translate(LanguageKey.use_new_cell_tower_location_key)}
                            {newCellTowerLocation && decisionOption === 3 && (
                              <span className="text-muted ms-2">
                                ({newCellTowerLocation.latitude.toFixed(6)},{" "}
                                {newCellTowerLocation.longitude.toFixed(6)})
                              </span>
                            )}
                            {!newCellTowerLocation && decisionOption === 3 && (
                              <small className="text-warning ms-2">
                                {translate(LanguageKey.please_select_location_on_map_key)}
                              </small>
                            )}
                            {decisionOption === 3 && (
                              <div className="text-muted mt-1">
                                <IcomoonIcon icon="info" size={14} className="me-1" />
                                {translate(LanguageKey.use_new_cell_tower_location_info_key)}
                              </div>
                            )}
                          </span>
                        }
                      />

                      <SntRadio
                        value={4}
                        selectedValue={decisionOption}
                        onChange={(value) => setDecisionOption(Number(value))}
                        label={translate(LanguageKey.clear_mismatches_without_adding_key)}
                      />
                    </VerticalLayout>
                  </div>
                </div>

                {/* Right Column - Map */}
                <div className="col-md-8">
                  <UpdateWifiCellularMismatchMap
                    items={items}
                    onClick={(data) => {
                      setNewCellTowerLocation({
                        latitude: data.latitude,
                        longitude: data.longitude,
                      });
                      setDecisionOption(3); // Automatically select "use new cell tower location" option
                    }}
                    showWifi={showWifi}
                    showLocationiq={showLocationiq}
                    showOpencell={showOpencell}
                    showHere={showHere}
                    showAdjacentTrustedPoints={showAdjacentTrustedPoints}
                    showHarvestedCellTower={showHarvestedCellTower}
                    showHarvestedLac={showHarvestedLac}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </SntDialog>

      <SntDialog
        isShow={isShowResponse}
        onSave={() => {
          setShowResponse(false);
        }}
        onClose={() => {
          setShowResponse(false);
        }}
        title={translate(LanguageKey.notifications_key)}
        saveTxt={"OK"}
        isShowCloseButton={false}
        btnSaveClassName="submit btn-sensolus"
      >
        {() => <div>{responseText}</div>}
      </SntDialog>

      <AdminLayout
        headerTitle={translate(LanguageKey.cell_tower_troubleshooting)}
        browserTabTitle={
          translate(LanguageKey.UserPermission_MENU_ADMIN_CELL_TOWER_TROUBLESHOOTING_description_key)
        }
        reloadSessionState={reloadSessionState}
        reload={reload}
        pageName={PAGE_NAME}
        sharedKey={PAGE_NAME}
        defaultOption={DEFAULT_OPTION}
        isAdvanceFilter={true}
        totalLabel={translate(LanguageKey.items_key)}
        toolList={toolList}
        handleActionData={handleActionData}
        body={({
          isDisabled = false,
          dataOptions,
          onChangeChildren,
        }: {
          isDisabled?: boolean;
          dataOptions?: Record<string, unknown>;
          onChangeChildren?: (a: unknown, b: unknown) => void;
        }): React.ReactElement | null =>
          dataOptions ? (
            <>
              {showResolveAll && (
                <div className="d-flex justify-content-end gap-2 mb-2">
                  <SntButton
                    onClick={() => onResolveAllClick()}
                    icon={<SntBulkIcon />}
                    text={translate(LanguageKey.resolve_all_key)} trackingKey="resolve_all_key"
                  />
                </div>
              )}

              <SntAdvancedSearchTableViewCard
                shared={(child: TableRef) => (table.current = child)}
                allColumns={allColumns}
                extraCols={extraCols}
                visibleCols={defaultColumnInit}
                disabled={isDisabled}
                onChange={(data: { params: Record<string, unknown>; dataOptions: Record<string, unknown> }) => {
                  if (onChangeChildren) {
                    onDataRequestChange(data, onChangeChildren);
                  }
                }}
                dataOptions={dataOptions}
                isDisabledCopyData={false}
                isDisabbledAddFilter={false}
                onFilterClicked={(
                  action: string,
                  key: string,
                  filterKeys: string[],
                  query: FilterQuery[]
                ) => {
                  setForceFilterAction({ action, key, filterKeys, query });
                }}
                filterMap={filterMap}
              />
            </>
          ) : null
        }
        forceFilterAction={forceFilterAction}
        setForceFilterAction={setForceFilterAction}
        onLoadFilter={(data: Record<string, unknown>) => setFilterMap(data)}
      ></AdminLayout>
    </div>
  );
};

export default LocationMismatch;
